import {
  Alert,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowCircleDown from "@mui/icons-material/ArrowCircleDown";
import BedroomWarningsPanel from "./BedroomWarningsPanel.jsx";
import BedroomGapsPanel from "./BedroomGapsPanel.jsx";

function SortIcon({ active, asc }) {
  if (!active) return null;
  return asc ? (
    <ArrowUpward sx={{ fontSize: 16, ml: 0.5 }} />
  ) : (
    <ArrowDownward sx={{ fontSize: 16, ml: 0.5 }} />
  );
}

function formatEnsuite(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "-";
}

function BedroomTableView({
  residents,
  noCampusSelected,
  availableRoomNames,
  validationErrors,
  getAssignedRoomName,
  getDraftRoomName,
  getAssignedRoomHouse,
  getAssignedRoomEnsuite,
  onAssignByRoomName,
  onClearRoom,
  onSort,
  sortField,
  sortAsc,
  onSave,
  isSaving,
  onDownloadFireList,
  onDownloadRoomPdf,
  onCreateRoom,
  warnings,
  onUndoWarning,
  onDismissWarning,
  onClearWarnings,
  coverageGaps,
}) {
  const renderSortButton = (field, label) => (
    <Button
      variant="text"
      onClick={() => onSort(field)}
      sx={{ px: 0, minWidth: 0, textTransform: "none", fontWeight: 700 }}
    >
      {label}
      <SortIcon active={sortField === field} asc={sortAsc} />
    </Button>
  );

  return (
    <Box>
      <BedroomGapsPanel gaps={coverageGaps} />
      <BedroomWarningsPanel
        warnings={warnings}
        onUndo={onUndoWarning}
        onDismiss={onDismissWarning}
        onClearAll={onClearWarnings}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{renderSortButton("name", "Resident")}</TableCell>
                <TableCell>Booking Group</TableCell>
                <TableCell>{renderSortButton("campus", "Campus")}</TableCell>
                <TableCell>{renderSortButton("age", "Age")}</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>House</TableCell>
                <TableCell>{renderSortButton("room", "Room")}</TableCell>
                <TableCell>Ensuite</TableCell>
                <TableCell sx={{ width: 320 }}>Assign / Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {noCampusSelected && (
                <TableRow>
                  <TableCell colSpan={9}>
                    Select a campus to see residents.
                  </TableCell>
                </TableRow>
              )}
              {residents.map((resident) => {
                const currentRoomName = getAssignedRoomName(resident.id);
                const currentHouse = getAssignedRoomHouse(resident.id);
                const currentEnsuite = getAssignedRoomEnsuite
                  ? getAssignedRoomEnsuite(resident.id)
                  : null;
                const draftRoomName = getDraftRoomName(resident.id);
                const validationError = validationErrors[resident.id] || null;

                return (
                  <TableRow key={resident.id} hover>
                    <TableCell>
                      {resident.name} {resident.surname}
                    </TableCell>
                    <TableCell>{resident.bookingGroupName || "-"}</TableCell>
                    <TableCell>{resident.campus || "-"}</TableCell>
                    <TableCell>{resident.age || "-"}</TableCell>
                    <TableCell>{resident.type}</TableCell>
                    <TableCell>{currentHouse || "-"}</TableCell>
                    <TableCell>{currentRoomName || "Unassigned"}</TableCell>
                    <TableCell>{formatEnsuite(currentEnsuite)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={draftRoomName}
                          placeholder="Enter house + room"
                          onChange={(e) =>
                            onAssignByRoomName(resident.id, e.target.value)
                          }
                          onBlur={(e) =>
                            onAssignByRoomName(resident.id, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Tab") {
                              onAssignByRoomName(
                                resident.id,
                                e.currentTarget.value
                              );
                            }
                          }}
                          helperText={validationError || " "}
                          error={Boolean(validationError)}
                          list={`room-options-${resident.id}`}
                        />
                        <datalist id={`room-options-${resident.id}`}>
                          {availableRoomNames.map((room) => (
                            <option key={room.value} value={room.value}>
                              {room.label}
                            </option>
                          ))}
                        </datalist>
                        <IconButton
                          size="small"
                          onClick={() => onClearRoom(resident.id)}
                          aria-label="Clear room assignment"
                        >
                          ×
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Button variant="contained" onClick={() => onSave(false)} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save Changes"}
              </Button>
              <Button variant="outlined" onClick={onDownloadFireList}>
                Download Fire List
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowCircleDown />}
                onClick={onDownloadRoomPdf}
              >
                Download Room PDF
              </Button>
              <Button variant="outlined" onClick={onCreateRoom}>
                + Create New Room
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Available Capacity
            </Typography>

            {availableRoomNames.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No rooms currently have spare capacity.
              </Typography>
            ) : (
              <List dense disablePadding>
                {availableRoomNames.map((room) => (
                  <ListItem key={room.value} disableGutters>
                    <ListItemText
                      primary={room.label}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Stack>
      </Box>

      {Object.keys(validationErrors).length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Some room assignments still need attention before saving.
        </Alert>
      )}
    </Box>
  );
}

export default BedroomTableView;